import React from "react";
import moment from "moment";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import type { ManualSoldOutImpact } from "services/scratcherService";
import { padLeftNumeric, parseBarcodeFormat } from "utils/barcodeFormat";
import { auditDashboardTheme } from "../../theme";

interface ManualSoldOutImpactNoticeProps {
  impact: ManualSoldOutImpact;
  storeId?: string | null;
  barcodeFormat?: string | null;
}

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);

const formatNumber = (value: number) => value.toLocaleString("en-US");

const formatGamePack = (
  gameId: string | number,
  packId: string | number,
  barcodeFormat?: string | null
) => {
  try {
    const pattern = parseBarcodeFormat(barcodeFormat);
    return {
      game: padLeftNumeric(gameId, pattern.game.length).slice(-pattern.game.length),
      pack: padLeftNumeric(packId, pattern.pack.length).slice(-pattern.pack.length),
    };
  } catch (_error) {
    return { game: String(gameId), pack: String(packId) };
  }
};

const linkSx = {
  color: auditDashboardTheme.colors.blue,
  textDecoration: "underline",
  textUnderlineOffset: "2px",
  "&:hover": {
    color: auditDashboardTheme.colors.text,
  },
};

export default function ManualSoldOutImpactNotice({
  impact,
  storeId,
  barcodeFormat,
}: ManualSoldOutImpactNoticeProps) {
  if (!impact.packs.length) {
    return null;
  }

  const markedDates = Array.from(new Set(impact.packs.map((pack) => pack.marked_date)));
  const summary = `${formatNumber(impact.total_tickets)} tickets | ${formatNumber(impact.pack_count)} packs | ${formatCurrency(Number(impact.total_retail_value))}`;

  return (
    <Box
      aria-label="Later manual sold out impacts"
      sx={{
        display: "grid",
        gap: "8px",
        px: "10px",
        py: "8px",
        border: "1px solid rgba(44, 108, 160, 0.22)",
        borderRadius: "8px",
        bgcolor: "rgba(239, 246, 255, 0.72)",
        overflowX: "auto",
      }}
    >
      <Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 2, alignItems: "center" }}>
        <Typography sx={{ color: auditDashboardTheme.colors.text, fontSize: 16, fontWeight: 700, lineHeight: 1.2 }}>
          Audit Updated Later
        </Typography>
        <Typography sx={{ color: auditDashboardTheme.colors.blue, fontSize: 15, fontWeight: 500, lineHeight: 1.2, whiteSpace: "nowrap", textAlign: "right" }}>
          {summary}
        </Typography>
      </Box>
      <Typography component="p" sx={{ m: 0, color: auditDashboardTheme.colors.textMutedStrong, fontSize: 13, fontWeight: 400, lineHeight: 1.35 }}>
        {`Audits for this date were updated when ${impact.pack_count} ${impact.pack_count === 1 ? "pack was" : "packs were"} manually marked sold out on `}
        {markedDates.map((markedDate, index) => (
          <React.Fragment key={markedDate}>
            {index > 0 ? ", " : null}
            <Box
              component={Link}
              to={`/store/${storeId}?from=${markedDate}&days=1&report_date=${markedDate}`}
              sx={linkSx}
            >
              {moment(markedDate).format("M/D/YY")}
            </Box>
          </React.Fragment>
        ))}
        .
      </Typography>
      <Box sx={{ display: "grid", gap: "5px", minWidth: 760 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(320px, 1fr) 130px 72px 88px 92px",
            gap: 1,
            color: auditDashboardTheme.colors.textMutedStrong,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: 1.2,
          }}
        >
          <Box>Game</Box><Box>Pack</Box><Box sx={{ textAlign: "right" }}>Tickets</Box>
          <Box sx={{ textAlign: "right" }}>Value</Box><Box sx={{ textAlign: "right" }}>Updated</Box>
        </Box>
        {impact.packs.map((pack) => {
          const formattedGamePack = formatGamePack(pack.game_id, pack.pack_id, barcodeFormat);

          return (
            <Box
              key={pack.audit_detail_id}
              sx={{
                display: "grid",
                gridTemplateColumns: "minmax(320px, 1fr) 130px 72px 88px 92px",
                gap: 1,
                alignItems: "center",
                color: auditDashboardTheme.colors.text,
                fontSize: 15,
                lineHeight: 1.35,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", minWidth: 0, whiteSpace: "nowrap" }}>
                <Box component={Link} to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle`} sx={{ ...linkSx, fontSize: 15, fontWeight: 500, lineHeight: 1.35 }}>
                  {pack.game_name || `Game ${pack.game_id}`}
                </Box>
                <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>•</Box>
                <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 15 }}>
                  {pack.tickets_per_pack == null ? "- tickets" : `${formatNumber(pack.tickets_per_pack)} tickets`}
                </Box>
                <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong }}>•</Box>
                <Box component="span" sx={{ color: auditDashboardTheme.colors.textMutedStrong, fontSize: 15 }}>
                  {formatCurrency(Number(pack.pack_value))}
                </Box>
              </Box>
              <Box
                component="span"
                sx={{
                  display: "inline-flex",
                  color: auditDashboardTheme.colors.textMutedStrong,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace !important',
                  fontSize: 15,
                  fontWeight: 400,
                  whiteSpace: "nowrap",
                  "& *": { fontFamily: "inherit !important" },
                }}
              >
                <Box component={Link} to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle`} sx={linkSx}>{formattedGamePack.game}</Box>
                <Box component="span">-</Box>
                <Box component={Link} to={`/store/${storeId}/scratcher/${pack.game_id}/packlifecycle/${pack.pack_id}?sold_out=true`} sx={linkSx}>{formattedGamePack.pack}</Box>
              </Box>
              <Box sx={{ textAlign: "right" }}>{formatNumber(pack.tickets)}</Box>
              <Box sx={{ textAlign: "right" }}>{formatCurrency(Number(pack.retail_value))}</Box>
              <Box
                component={Link}
                to={`/store/${storeId}?from=${pack.marked_date}&days=1&report_date=${pack.marked_date}`}
                sx={{ ...linkSx, textAlign: "right" }}
              >
                {moment(pack.marked_date).format("M/D/YY")}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
